'use client'

import { clsx } from 'clsx'
import { type FC, type RefObject, useEffect, useRef, useState } from 'react'

import { ProductVoucherReadonlyLabel } from '../../../product'
import { ProductSearchFlag } from '../ProductSearchFlag'

import { useProductSearchResultItemContext } from './ProductSearchResultItemContext/productSearchResultItemContext'
import { ProductSearchResultItemNowLabel } from './ProductSearchResultItemLabels/ProductSearchResultItemNowLabel'
import { ProductSearchResultItemPrescriptionRequiredLabel } from './ProductSearchResultItemLabels/ProductSearchResultItemPrescriptionRequiredLabel'
import { ProductSearchResultItemPromotionLabelRenderer } from './ProductSearchResultItemLabels/ProductSearchResultItemPromotionLabelRenderer'
import { ProductSearchResultItemRedPointsLabel } from './ProductSearchResultItemLabels/ProductSearchResultItemRedPointsLabel'
import { ProductSearchResultItemSellerPromotionLabel } from './ProductSearchResultItemLabels/ProductSearchResultItemSellerPromotionLabel'
import { ProductSearchResultItemSellerSponsoredLabel } from './ProductSearchResultItemLabels/ProductSearchResultItemSellerSponsoredLabel'
import {
  ProductSearchResultItemVolumePricingLabel,
  useProductSearchShouldRenderVolumePricingLabel,
} from './ProductSearchResultItemLabels/ProductSearchResultItemVolumePricingLabel'
import { useProductSearchResultItemLabels } from './ProductSearchResultItemLabels/useProductSearchResultItemLabels'

import styles from './ProductSearchResultItemLabelList.module.css'

// if offsetTop > BASE_OFFSET it means the item is wrapped and hidden
const BASE_OFFSET = 2
const ITEMS_GAP = 12

const useResultItemLabelReducer = (): {
  containerRef: RefObject<HTMLDivElement | null>
  counterRef: RefObject<HTMLDivElement | null>
  hiddenCount: number
} => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const counterRef = useRef<HTMLDivElement | null>(null)
  const [hiddenCount, setHiddenCount] = useState<number>(0)

  useEffect(() => {
    const checkWidth = (): void => {
      let hiddenLabelTmpCounter = 0
      setHiddenCount(hiddenLabelTmpCounter)
      if (!containerRef.current) {
        return
      }

      const referenceOffset =
        containerRef.current.parentElement?.getBoundingClientRect().x ?? 0
      let counterLeft = 0

      for (const item of containerRef.current.children) {
        if (item instanceof HTMLElement) {
          if (item.offsetTop > BASE_OFFSET) {
            hiddenLabelTmpCounter += 1
            setHiddenCount(hiddenLabelTmpCounter)
          } else {
            counterLeft =
              item.getBoundingClientRect().right + ITEMS_GAP - referenceOffset
          }
        }
      }

      if (counterRef.current instanceof HTMLElement) {
        counterRef.current.style.left = `${counterLeft}px`
      }
    }

    checkWidth()

    // eslint-disable-next-line unicorn/prefer-observer-apis -- `ResizeObserver` is not supported by our browsers
    window.addEventListener('resize', checkWidth)

    return (): void => {
      window.removeEventListener('resize', checkWidth)
    }
  }, [])

  return {
    containerRef,
    counterRef,
    hiddenCount,
  }
}

export const ProductSearchResultItemLabelList: FC = () => {
  const { algoliaRecord } = useProductSearchResultItemContext()

  const resultItemLabels = useProductSearchResultItemLabels({ algoliaRecord })

  const { containerRef, counterRef, hiddenCount } = useResultItemLabelReducer()

  const renderVolumeBasedPricingLabel =
    useProductSearchShouldRenderVolumePricingLabel()

  return (
    <div className="absolute inset-x-3 top-0 z-10 flex -translate-y-1/2 pr-12">
      <div
        className={clsx(
          styles['product-search-result-item-label-list__container'],
          'flex flex-wrap items-end gap-3 overflow-hidden',
        )}
        ref={containerRef}
      >
        {resultItemLabels.map((label) => {
          switch (label.type) {
            case 'now': {
              return <ProductSearchResultItemNowLabel key={label.type} />
            }
            case 'prescription_medicine': {
              return (
                <ProductSearchResultItemPrescriptionRequiredLabel
                  key={label.type}
                />
              )
            }
            case 'promotion': {
              return (
                <ProductSearchResultItemPromotionLabelRenderer
                  key={label.type}
                  label={label}
                />
              )
            }
            case 'redPointsMultiplier': {
              return (
                <ProductSearchResultItemRedPointsLabel
                  key={label.type}
                  label={label}
                />
              )
            }
            case 'sellerPromotion': {
              return (
                <ProductSearchResultItemSellerPromotionLabel key={label.type} />
              )
            }
            case 'sponsored': {
              return (
                <ProductSearchResultItemSellerSponsoredLabel key={label.type} />
              )
            }
            case 'volumeBasedPricing': {
              return renderVolumeBasedPricingLabel ? (
                <ProductSearchResultItemVolumePricingLabel key={label.type} />
              ) : null
            }
            case 'voucher': {
              return (
                <ProductVoucherReadonlyLabel
                  id={algoliaRecord.upid}
                  key={label.type}
                />
              )
            }
            default: {
              return (
                <ProductSearchFlag
                  className="border-dark-primary-min bg-light-primary-low"
                  key={label.type}
                >
                  {label.labelText}
                </ProductSearchFlag>
              )
            }
          }
        })}
      </div>
      {resultItemLabels.length > 0 ? (
        <div
          className={clsx('absolute', !hiddenCount && 'hidden')}
          ref={counterRef}
        >
          <ProductSearchFlag className="border-dark-primary-min bg-light-primary-low">
            {`+${hiddenCount}`}
          </ProductSearchFlag>
        </div>
      ) : null}
    </div>
  )
}
